import { ApprovalQueueMeta, ApprovalQueueRow, DateRange, RangePreset } from './approval-queue.models';

const MS_PER_DAY = 86400000;

/**
 * Whole days between two YYYY-MM-DD dates. Parsed as UTC midnight so a DST
 * boundary inside the span can't shave a day off the result.
 * @param from earlier date
 * @param to later date (normally meta.as_of — the server's today)
 */
export function daysSince(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / MS_PER_DAY);
}

/** Shifts a YYYY-MM-DD date by whole days, in UTC. */
function shiftDays(date: string, delta: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + delta * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);
}

/**
 * The date range a preset stands for. Windows are inclusive on both ends, so
 * "last 7 days" spans today and the six days before it.
 * @param preset which preset
 * @param today YYYY-MM-DD, normally meta.as_of
 */
export function rangeForPreset(preset: RangePreset, today: string): DateRange {
  switch (preset) {
    case 'last7':
      return { startDate: shiftDays(today, -6), endDate: today };
    case 'last30':
      return { startDate: shiftDays(today, -29), endDate: today };
    // 'custom' carries its own dates; the control supplies them directly.
    case 'custom':
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }
}

/** Which preset a range corresponds to, for restoring the control from the URL. */
export function presetForRange(range: DateRange, today: string): RangePreset {
  if (!range.startDate && !range.endDate) return 'all';
  const last7 = rangeForPreset('last7', today);
  if (range.startDate === last7.startDate && range.endDate === last7.endDate) return 'last7';
  const last30 = rangeForPreset('last30', today);
  if (range.startDate === last30.startDate && range.endDate === last30.endDate) return 'last30';
  return 'custom';
}

/**
 * Query params for the drill-down into a project's Sites tab, pre-filtered to
 * the awaiting-approval bucket. The param names match what
 * site-wise-reports.component.ts already reads, so the active range carries
 * through untouched.
 */
export function drillDownParams(range: DateRange): Record<string, string> {
  const params: Record<string, string> = {
    tab: 'sites',
    status: 'submitted-reviewed',
  };
  if (range.startDate) params.startDate = range.startDate;
  if (range.endDate) params.endDate = range.endDate;
  return params;
}

/**
 * Splits the server-ordered rows into the two divider groups, preserving the
 * order the server chose within each.
 */
export function partitionByGroup(rows: ApprovalQueueRow[]): {
  recent: ApprovalQueueRow[];
  stale: ApprovalQueueRow[];
} {
  return {
    recent: rows.filter(r => r.group === 'recent'),
    stale: rows.filter(r => r.group === 'stale'),
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jan 1" — no year. */
function shortDay(date: string): string {
  const [, m, d] = date.split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
}

/** "Jan 1, 2026". */
function shortDate(date: string): string {
  return `${shortDay(date)}, ${date.slice(0, 4)}`;
}

/**
 * The Waiting column header. Naming the active range in the header is what
 * stops the count reading as the project's whole backlog.
 */
export function waitingColumnLabel(range: DateRange): string {
  const { startDate, endDate } = range;
  if (!startDate && !endDate) return 'Waiting';
  if (startDate && endDate) {
    const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
    return sameYear
      ? `Waiting · ${shortDay(startDate)} – ${shortDate(endDate)}`
      : `Waiting · ${shortDate(startDate)} – ${shortDate(endDate)}`;
  }
  if (startDate) return `Waiting · from ${shortDate(startDate)}`;
  return `Waiting · through ${shortDate(endDate)}`;
}

export const RANGE_PRESETS: { key: RangePreset; name: string }[] = [
  { key: 'all', name: 'All time' },
  { key: 'last7', name: 'Last 7 days' },
  { key: 'last30', name: 'Last 30 days' },
  { key: 'custom', name: 'Custom' },
];

/**
 * The chip's text. Mirrors the executive dashboard's period chip: preset name,
 * then the range it resolves to — "Last 30 days · Aug 19 – Sep 17". A custom
 * range reads "Custom · Sep 2 – Sep 17"; no range reads "All time".
 */
export function rangeChipText(range: DateRange, today: string): string {
  const preset = presetForRange(range, today);
  const name = RANGE_PRESETS.find(p => p.key === preset)?.name || 'All time';
  if (preset === 'all' || !range.startDate || !range.endDate) return name;
  const sameYear = range.startDate.slice(0, 4) === range.endDate.slice(0, 4);
  const from = sameYear ? shortDay(range.startDate) : shortDate(range.startDate);
  return `${name} · ${from} – ${sameYear ? shortDay(range.endDate) : shortDate(range.endDate)}`;
}

/** Whether a row can be placed on the map. Coordinates arrive as strings from the API. */
export function hasCoordinates(row: ApprovalQueueRow): boolean {
  const lat = Number(row.latitude);
  const lng = Number(row.longitude);
  return row.latitude !== null && row.longitude !== null
    && Number.isFinite(lat) && Number.isFinite(lng)
    && lat !== 0 && lng !== 0;
}

/**
 * Hover tooltip for a queue pin. Same fact set as the row — name, waiting count,
 * oldest waiting date — and the same tooltip classes the /reports map uses so it
 * inherits that styling.
 */
export function pinTooltipHtml(row: ApprovalQueueRow): string {
  const noun = row.waiting_count === 1 ? 'report' : 'reports';
  const oldest = row.oldest_report_date ? ` · oldest ${shortDay(row.oldest_report_date)}` : '';
  return `<span class="text-type-15 tooltip-bold" style="color: #060b1e;">${escapeHtml(row.project_name)}</span>`
    + `<span class="text-type-15 tooltip-light" style="font-weight: 400;"> · ${row.waiting_count} ${noun} waiting${oldest}</span>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * True when the user can never approve anywhere, so the /projects entry strip
 * skips its probe (spec §7). `roles` is one row per project, so a user only
 * never approves when EVERY row is a contractor or reviewer role — a PM here
 * and a contractor there still has a queue. No rows at all also means no
 * queue: the API scopes by project role.
 */
export function neverApproves(roles: { role?: string | null }[] | null | undefined): boolean {
  return (roles || []).every(r => {
    const code = r?.role || '';
    return code === 'reviewer' || code.startsWith('contractor');
  });
}

/**
 * Whether the /projects entry strip renders for this probe result (spec §7).
 * The queue is cross-project, so a user who can approve on only one project
 * sees nothing here: that project's reports tab already shows the same count.
 */
export function showsReviewStrip(meta: ApprovalQueueMeta | null | undefined): boolean {
  return (meta?.approver_project_count || 0) > 1 && (meta?.total_waiting || 0) > 0;
}
